/**
 * Team Members — Workforce Management primary UX (Phase G.2).
 *
 * Scope: cards + table views, search + 5 filters, quick actions per row,
 * side-drawer profile workspace (7 tabs), Access Summary highlighting the
 * chain: Template → Role → Extras → Overrides.
 *
 * Deferred (later phases): Password Reset, Invitations, CSV Import, MFA,
 * Impersonation, Documents, Payroll, Notes, Emergency Contacts.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Search, Filter as FilterIcon, LayoutGrid, List, Pause, Play, Pencil,
  ShieldCheck, Star, Users, Building2, Briefcase, ChevronRight, MoreVertical,
  UserPlus, RefreshCw, Sparkles, X, Trash2, Loader2, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState, Skeleton, Section } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TeamMemberDrawer from "./TeamMemberDrawer";
import OnboardingWizard from "./OnboardingWizard";

const STATUS_TONE = {
  active:        "success",
  suspended:     "error",
  on_leave:      "warning",
  notice_period: "warning",
  terminated:    "neutral",
};

/* ── initials avatar ─────────────────────────────────────────────────── */
function Avatar({ name, photo_url, size = 44 }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2)
                                  .map(w => w[0].toUpperCase()).join("");
  if (photo_url) {
    return <img src={photo_url} alt={name} style={{ width: size, height: size }}
                 className="rounded-xl object-cover ring-2 ring-white shadow-sm"/>;
  }
  return (
    <div style={{ width: size, height: size }}
         className="rounded-xl bg-gradient-to-br from-hocp-honeydew to-hocp-vanilla text-hocp-navy
                    flex items-center justify-center font-display font-bold text-[15px] shadow-sm">
      {initials || "?"}
    </div>
  );
}

/* ── Access summary card (the single place managers see access) ──────── */
function AccessSummaryCard({ tm }) {
  const snap = tm.access_template_snapshot;
  const active = tm.active_assignments || [];
  const primary = active.find(a => a.is_primary);
  const additional = active.filter(a => !a.is_primary && a.status === "active");

  return (
    <div className="rounded-xl border border-hocp-border p-3 bg-hocp-alice/20 space-y-2"
         data-testid={`access-summary-${tm.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-hocp-textTertiary">
          Access Summary
        </div>
        {snap && (
          <Badge tone="accent" size="sm">
            <ShieldCheck className="w-3 h-3 mr-0.5" strokeWidth={2}/> {snap.name}
          </Badge>
        )}
      </div>
      <div className="space-y-1.5 text-[12px]">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-hocp-textTertiary shrink-0">Role</span>
          <span className="font-medium text-hocp-textPrimary text-right truncate" data-testid={`tm-role-${tm.id}`}>
            {typeof tm.role_name === "string" && tm.role_name ? tm.role_name : "Staff"}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-hocp-textTertiary shrink-0">Primary Dept</span>
          <span className="font-medium text-hocp-textPrimary flex items-center gap-1 text-right truncate" data-testid={`tm-dept-${tm.id}`}>
            {primary ? (
              <>
                <Star className="w-3 h-3 text-hocp-honeydew shrink-0" strokeWidth={2}/>
                {primary.department_name || primary.department_code}
              </>
            ) : (
              <span className="text-hocp-textTertiary">Unassigned</span>
            )}
          </span>
        </div>
      </div>
      {additional.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[11px] text-hocp-textTertiary">Also:</span>
          {additional.slice(0, 4).map(a => (
            <span key={a.id} className="text-[11px] px-1.5 py-0.5 rounded-md bg-hocp-alice/60 text-hocp-textPrimary">
              {a.department_name || a.department_code}
            </span>
          ))}
          {additional.length > 4 && (
            <span className="text-[11px] text-hocp-textTertiary">+{additional.length - 4}</span>
          )}
        </div>
      )}
      {(snap || tm.permission_overrides?.length > 0) && (
        <div className="flex items-center gap-3 pt-1 text-[11px] text-hocp-textSecondary">
          {snap && <span>{snap.extra_permissions?.length || 0} template perms</span>}
          {tm.permission_overrides?.length > 0 && (
            <span className="text-hocp-textPrimary font-medium">
              {tm.permission_overrides.length} override{tm.permission_overrides.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── card view ───────────────────────────────────────────────────────── */
const AVAILABILITY_TONE = {
  available: "success", on_duty: "info", busy: "warning",
  on_break: "warning", off_duty: "neutral", training: "info",
};
const AVAILABILITY_LABEL = {
  available: "Available", on_duty: "On Duty", busy: "Busy",
  on_break: "On Break", off_duty: "Off Duty", training: "Training",
};

function TeamCard({ tm, onOpen, onSuspend, onReactivate, onDelete, canSuspend, canDelete, availability, currentUserId }) {
  const status = tm.employment_status || (tm.is_active ? "active" : "suspended");
  return (
    <div className="hocp-card p-5 flex flex-col gap-4 hover:-translate-y-[1px] transition-all"
         data-testid={`team-card-${tm.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={tm.name} photo_url={tm.photo_url} size={52}/>
        <div className="flex-1 min-w-0">
          <button className="text-left font-display font-semibold text-[16px] text-hocp-textPrimary truncate hover:underline"
                  onClick={() => onOpen(tm)} data-testid={`team-open-${tm.id}`}>
            {tm.name || tm.email}
          </button>
          <div className="text-[12px] text-hocp-textSecondary truncate">{tm.designation || "—"}</div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Badge tone={STATUS_TONE[status] || "neutral"} size="sm">{status}</Badge>
            {tm.employment_type && (
              <Badge tone="neutral" size="sm">{tm.employment_type.toLowerCase()}</Badge>
            )}
            {availability && (
              <Badge tone={AVAILABILITY_TONE[availability]} size="sm"
                     data-testid={`team-avail-${tm.id}`}>
                {AVAILABILITY_LABEL[availability]}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="text-[11px] font-mono text-hocp-textTertiary tabular-nums">{tm.employee_id || "—"}</div>

      <AccessSummaryCard tm={tm}/>

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-hocp-alice/60">
        <button
          onClick={() => onOpen(tm)}
          className="text-[12px] font-medium text-hocp-textSecondary hover:text-hocp-textPrimary flex items-center gap-1"
          data-testid={`team-edit-${tm.id}`}>
          <Pencil className="w-3.5 h-3.5"/> Edit
        </button>
        {canSuspend && (
          status === "active" ? (
            <button onClick={() => onSuspend(tm)}
                    className="text-[12px] font-medium text-hocp-error hover:opacity-80 flex items-center gap-1"
                    data-testid={`team-suspend-${tm.id}`}>
              <Pause className="w-3.5 h-3.5"/> Suspend
            </button>
          ) : (
            <button onClick={() => onReactivate(tm)}
                    className="text-[12px] font-medium text-hocp-success hover:opacity-80 flex items-center gap-1"
                    data-testid={`team-reactivate-${tm.id}`}>
              <Play className="w-3.5 h-3.5"/> Reactivate
            </button>
          )
        )}
        {canDelete && tm.id !== currentUserId && (
          <button onClick={() => onDelete(tm)}
                  className="text-[12px] font-medium text-hocp-error hover:opacity-80 flex items-center gap-1"
                  data-testid={`team-delete-${tm.id}`}
                  title="Delete team member">
            <Trash2 className="w-3.5 h-3.5"/> Delete
          </button>
        )}
        <button onClick={() => onOpen(tm)}
                className="text-[12px] font-medium text-hocp-textSecondary hover:text-hocp-textPrimary flex items-center gap-1">
          Open <ChevronRight className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}

/* ── table view row ──────────────────────────────────────────────────── */
function TeamTableRow({ tm, onOpen, onSuspend, onReactivate, onDelete, canSuspend, canDelete, availability, currentUserId }) {
  const status = tm.employment_status || (tm.is_active ? "active" : "suspended");
  const primary = (tm.active_assignments || []).find(a => a.is_primary);
  const additional = (tm.active_assignments || []).filter(a => !a.is_primary && a.status === "active");

  return (
    <tr className="border-t border-hocp-border hover:bg-hocp-alice/25 transition-colors"
        data-testid={`team-row-${tm.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={tm.name} photo_url={tm.photo_url} size={36}/>
          <div className="min-w-0">
            <button onClick={() => onOpen(tm)}
                    className="text-[13px] font-medium text-hocp-textPrimary hover:underline truncate block"
                    data-testid={`team-open-${tm.id}`}>{tm.name || tm.email}</button>
            <div className="text-[11px] text-hocp-textTertiary truncate">{tm.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-[12px] font-mono tabular-nums text-hocp-textSecondary">{tm.employee_id || "—"}</td>
      <td className="px-4 py-3">
        <div className="text-[13px] text-hocp-textPrimary">{tm.designation || "—"}</div>
        <div className="text-[11px] text-hocp-textTertiary">{tm.role_name || "user"}</div>
      </td>
      <td className="px-4 py-3">
        {primary ? (
          <div className="flex items-center gap-1 text-[13px] text-hocp-textPrimary">
            <Star className="w-3 h-3 text-hocp-honeydew" strokeWidth={2}/>
            {primary.department_name || primary.department_code}
          </div>
        ) : <span className="text-[12px] text-hocp-textTertiary">Unassigned</span>}
        {additional.length > 0 && (
          <div className="text-[11px] text-hocp-textTertiary mt-0.5">+{additional.length} more</div>
        )}
      </td>
      <td className="px-4 py-3">
        {tm.access_template_snapshot?.name ? (
          <Badge tone="accent" size="sm">{tm.access_template_snapshot.name}</Badge>
        ) : <span className="text-[12px] text-hocp-textTertiary">None</span>}
      </td>
      <td className="px-4 py-3">
        <Badge tone={STATUS_TONE[status] || "neutral"} size="sm">{status}</Badge>
        {availability && (
          <div className="mt-1">
            <Badge tone={AVAILABILITY_TONE[availability]} size="sm"
                   data-testid={`team-row-avail-${tm.id}`}>
              {AVAILABILITY_LABEL[availability]}
            </Badge>
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onOpen(tm)} data-testid={`team-edit-${tm.id}`}>
            <Pencil className="w-3.5 h-3.5"/>
          </Button>
          {canSuspend && (status === "active" ? (
            <Button variant="ghost" size="sm" onClick={() => onSuspend(tm)} data-testid={`team-suspend-${tm.id}`}>
              <Pause className="w-3.5 h-3.5"/>
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => onReactivate(tm)} data-testid={`team-reactivate-${tm.id}`}>
              <Play className="w-3.5 h-3.5"/>
            </Button>
          ))}
          {canDelete && tm.id !== currentUserId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(tm)}
              data-testid={`team-delete-${tm.id}`}
              className="text-hocp-error hover:text-hocp-error hover:bg-red-50"
              title="Delete team member"
            >
              <Trash2 className="w-3.5 h-3.5"/>
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ── main page ───────────────────────────────────────────────────────── */
export default function TeamMembers() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(new Set());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cards");            // "cards" | "table"
  const [drawerId, setDrawerId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // reference data
  const [templates,   setTemplates]   = useState([]);
  const [departments, setDepartments] = useState([]);
  const [empTypes,    setEmpTypes]    = useState([]);

  // filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [deptFilter, setDept] = useState("all");
  const [typeFilter, setType] = useState("all");
  const [templateFilter, setTemplate] = useState("all");
  const [availFilter, setAvail] = useState("all");
  const [skillFilter, setSkill] = useState("all");
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [memberSkills, setMemberSkills] = useState({});
  const [skillCatalog, setSkillCatalog] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter !== "all")   params.set("status", statusFilter);
      if (deptFilter !== "all")     params.set("department_id", deptFilter);
      if (typeFilter !== "all")     params.set("employment_type", typeFilter);
      if (templateFilter !== "all") params.set("access_template", templateFilter);
      const { data } = await api.get(`/team-members?${params.toString()}`);
      setRows(data);
      // Fetch availability + skills for the returned members (best-effort)
      const availMap = {};
      const skillMap = {};
      await Promise.all(data.slice(0, 60).map(async (m) => {
        try {
          const { data: av } = await api.get(`/team-members/${m.id}/availability`);
          availMap[m.id] = av.status;
        } catch { /* silent */ }
        try {
          const { data: sk } = await api.get(`/team-members/${m.id}/skills`);
          skillMap[m.id] = (sk || []).map(s => s.skill_code);
        } catch { /* silent */ }
      }));
      setAvailabilityMap(availMap);
      setMemberSkills(skillMap);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load team members");
    } finally { setLoading(false); }
  }, [q, statusFilter, deptFilter, typeFilter, templateFilter]);

  // initial + on-filter refresh
  useEffect(() => { refresh(); }, [statusFilter, deptFilter, typeFilter, templateFilter]); // eslint-disable-line

  // load reference data once
  useEffect(() => {
    (async () => {
      try {
        const [tRes, dRes, eRes, meRes, skRes] = await Promise.all([
          api.get("/access-templates"),
          api.get("/departments"),
          api.get("/employment-types"),
          api.get("/auth/me"),
          api.get("/readiness/skills/catalog"),
        ]);
        setTemplates(tRes.data || []);
        setDepartments(dRes.data || []);
        setEmpTypes(eRes.data || []);
        setPermissions(new Set(meRes.data?.permissions || []));
        setSkillCatalog(skRes.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const hasPerm = (code) => permissions.has("*") || permissions.has(code) || user?.role === "super_admin";

  const onSuspend = async (tm) => {
    const reason = window.prompt(`Suspend ${tm.name}? Reason:`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/team-members/${tm.id}/suspend`, { reason });
      toast.success(`${tm.name} suspended`);
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed to suspend"); }
  };
  const onReactivate = async (tm) => {
    try {
      await api.post(`/team-members/${tm.id}/reactivate`);
      toast.success(`${tm.name} reactivated`);
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed to reactivate"); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/team-members/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name || deleteTarget.email} permanently deleted`);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to delete team member");
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setQ(""); setStatus("all"); setDept("all"); setType("all"); setTemplate("all");
    setAvail("all"); setSkill("all");
  };

  // Client-side apply of readiness filters + auto-hide terminated members.
  // Deleted (terminated) members are preserved on the backend for audit but
  // should not appear in the day-to-day roster.
  const displayRows = useMemo(() => {
    if (!rows) return rows;
    let out = rows.filter((r) => r.employment_status !== "terminated");
    if (availFilter !== "all") {
      out = out.filter(r => availabilityMap[r.id] === availFilter);
    }
    if (skillFilter !== "all") {
      out = out.filter(r => (memberSkills[r.id] || []).includes(skillFilter));
    }
    return out;
  }, [rows, availFilter, skillFilter, availabilityMap, memberSkills]);

  const stats = useMemo(() => {
    if (!rows) return null;
    const visible = rows.filter((r) => r.employment_status !== "terminated");
    return {
      total:      visible.length,
      active:     visible.filter(r => r.employment_status === "active").length,
      suspended:  visible.filter(r => r.employment_status === "suspended").length,
      unassigned: visible.filter(r => !r.primary_department_id).length,
    };
  }, [rows]);

  const hasAnyFilter = q || statusFilter !== "all" || deptFilter !== "all"
    || typeFilter !== "all" || templateFilter !== "all"
    || availFilter !== "all" || skillFilter !== "all";

  return (
    <div className="space-y-6" data-testid="team-members-page">
      <PageHeader
        title="Team Members"
        subtitle="Workforce — hire, assign departments, apply access templates."
        action={
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-hocp-alice/50 rounded-lg p-0.5 border border-hocp-border">
              <button onClick={() => setView("cards")}
                      className={`px-2.5 py-1 rounded-md text-[12px] font-medium flex items-center gap-1 transition
                        ${view === "cards" ? "bg-white shadow-sm text-hocp-textPrimary" : "text-hocp-textSecondary"}`}
                      data-testid="view-cards">
                <LayoutGrid className="w-3.5 h-3.5"/> Cards
              </button>
              <button onClick={() => setView("table")}
                      className={`px-2.5 py-1 rounded-md text-[12px] font-medium flex items-center gap-1 transition
                        ${view === "table" ? "bg-white shadow-sm text-hocp-textPrimary" : "text-hocp-textSecondary"}`}
                      data-testid="view-table">
                <List className="w-3.5 h-3.5"/> Table
              </button>
            </div>
            <Button size="sm" onClick={() => setWizardOpen(true)}
                     data-testid="team-onboard" disabled={!hasPerm("team_members.create")}>
              <UserPlus className="w-4 h-4 mr-1"/> Hire Team Member
            </Button>
          </div>
        }
        testid="page-team"
      />

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="team-stats-grid">
          <Card padded testid="stat-total">
            <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary">Total</div>
            <div className="text-2xl font-semibold text-hocp-textPrimary mt-0.5 tabular-nums">{stats.total}</div>
          </Card>
          <Card padded testid="stat-active">
            <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary">Active</div>
            <div className="text-2xl font-semibold text-hocp-success mt-0.5 tabular-nums">{stats.active}</div>
          </Card>
          <Card padded testid="stat-suspended">
            <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary">Suspended</div>
            <div className="text-2xl font-semibold text-hocp-error mt-0.5 tabular-nums">{stats.suspended}</div>
          </Card>
          <Card padded testid="stat-unassigned">
            <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary">Unassigned</div>
            <div className="text-2xl font-semibold text-hocp-textPrimary mt-0.5 tabular-nums">{stats.unassigned}</div>
          </Card>
        </div>
      )}

      {/* Filter bar */}
      <Card padded testid="team-filters">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-hocp-textTertiary"/>
            <Input
              placeholder="Search name, email, employee ID…"
              aria-label="Search team members"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") refresh(); }}
              className="pl-9"
              data-testid="team-search"
            />
          </div>
          <Select value={deptFilter} onValueChange={setDept}>
            <SelectTrigger data-testid="filter-department" aria-label="Filter by department"><SelectValue placeholder="Department"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" aria-label="Filter by status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_leave">On leave</SelectItem>
              <SelectItem value="notice_period">Notice period</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type" aria-label="Filter by employment type"><SelectValue placeholder="Employment"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {empTypes.map(t => (
                <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={templateFilter} onValueChange={setTemplate}>
            <SelectTrigger data-testid="filter-template" aria-label="Filter by access template"><SelectValue placeholder="Template"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.code}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={availFilter} onValueChange={setAvail}>
              <SelectTrigger className="w-[180px]" data-testid="filter-availability" aria-label="Filter by availability"><SelectValue placeholder="Availability"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All availability</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="on_duty">On duty</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="on_break">On break</SelectItem>
                <SelectItem value="off_duty">Off duty</SelectItem>
                <SelectItem value="training">Training</SelectItem>
              </SelectContent>
            </Select>
            <Select value={skillFilter} onValueChange={setSkill}>
              <SelectTrigger className="w-[200px]" data-testid="filter-skill" aria-label="Filter by skill"><SelectValue placeholder="Skill"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any skill</SelectItem>
                {skillCatalog.map(s => (
                  <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            {hasAnyFilter && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                <X className="w-3.5 h-3.5 mr-1"/> Clear
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={refresh} data-testid="team-apply">
              <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Results */}
      {loading && !rows && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-card-skeleton">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="hocp-card p-5 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="w-12 h-12 rounded-xl"/>
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40"/>
                  <Skeleton className="h-3 w-56"/>
                </div>
              </div>
              <Skeleton className="h-24 w-full"/>
            </div>
          ))}
        </div>
      )}

      {!loading && displayRows && displayRows.length === 0 && (
        <EmptyState icon={Users} title="No team members match these filters"
                     subtitle="Try clearing filters or search by a different keyword."
                     action={hasAnyFilter && (
                       <Button size="sm" variant="outline" onClick={clearFilters}>Clear filters</Button>
                     )}/>
      )}

      {displayRows && displayRows.length > 0 && view === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-card-grid">
          {displayRows.map(tm => (
            <TeamCard key={tm.id} tm={tm}
                       availability={availabilityMap[tm.id]}
                       currentUserId={user?.id}
                       onOpen={m => setDrawerId(m.id)}
                       onSuspend={onSuspend} onReactivate={onReactivate}
                       onDelete={setDeleteTarget}
                       canSuspend={hasPerm("team_members.suspend")}
                       canDelete={hasPerm("team_members.deactivate")}/>
          ))}
        </div>
      )}

      {displayRows && displayRows.length > 0 && view === "table" && (
        <Card testid="team-table">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-hocp-alice/40">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Name</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Employee ID</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Designation / Role</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Primary Department</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Template</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(tm => (
                  <TeamTableRow key={tm.id} tm={tm}
                                 availability={availabilityMap[tm.id]}
                                 currentUserId={user?.id}
                                 onOpen={m => setDrawerId(m.id)}
                                 onSuspend={onSuspend} onReactivate={onReactivate}
                                 onDelete={setDeleteTarget}
                                 canSuspend={hasPerm("team_members.suspend")}
                                 canDelete={hasPerm("team_members.deactivate")}/>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Drawer */}
      <TeamMemberDrawer
        memberId={drawerId}
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        onChanged={refresh}
        templates={templates}
        departments={departments}
        employmentTypes={empTypes}
        permissions={permissions}
        isSuperAdmin={user?.role === "super_admin"}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-hocp-error"/>
              Delete this team member?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-hocp-textPrimary">
                {deleteTarget?.name || deleteTarget?.email}
              </span>{" "}
              will be <span className="font-semibold text-hocp-error">permanently deleted</span> from
              this hotel — their profile, access and sessions are erased and the email
              becomes available for a new hire. Only the audit log keeps a record.
              This cannot be undone. Use <span className="font-medium">Suspend</span> if
              you may need to reinstate them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="team-delete-confirm"
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
              Delete team member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Onboarding Wizard */}
      <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(r) => {
          refresh();
          if (r?.openProfile) setDrawerId(r.openProfile);
        }}
        templates={templates}
        departments={departments}
        employmentTypes={empTypes}
      />
    </div>
  );
}
